import React, { useState, useEffect } from 'react';
import api from '../config/api';
import AIConfigurationModal from './AIConfigurationModal';

function AIConfiguration() {
  const [configurations, setConfigurations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingConfiguration, setEditingConfiguration] = useState(null);
  const [testingConfiguration, setTestingConfiguration] = useState(null);

  useEffect(() => {
    fetchConfigurations();
  }, []);

  const fetchConfigurations = async () => {
    try {
      console.log('Fetching AI configurations from:', '/ai-configurations');
      const response = await api.get('/ai-configurations');
      console.log('Successfully fetched configurations:', response.data);
      setConfigurations(response.data);
    } catch (error) {
      console.error('Error fetching AI configurations:', error);
      console.error('Error details:', error.response?.data);
      console.error('Status:', error.response?.status);
      console.error('URL:', error.config?.url);
      alert('Error fetching AI configurations: ' + (error.response?.data?.error || error.response?.statusText || error.message));
    } finally {
      setLoading(false);
    }
  };

  const saveConfiguration = async (configurationData) => {
    try {
      console.log('Saving configuration data:', configurationData);
      if (editingConfiguration) {
        console.log('Updating configuration with PUT to:', `/ai-configurations/${editingConfiguration.id}`);
        const response = await api.put(`/ai-configurations/${editingConfiguration.id}`, configurationData);
        console.log('Update response:', response.data);
        setConfigurations(configurations.map(config => 
          config.id === editingConfiguration.id ? response.data : config
        ));
      } else {
        console.log('Creating configuration with POST to:', '/ai-configurations');
        const response = await api.post('/ai-configurations', configurationData);
        console.log('Create response:', response.data);
        setConfigurations([...configurations, response.data]);
      }
      setShowModal(false);
      setEditingConfiguration(null);
      
      // If this configuration was set as active, refresh the list to update active states
      if (configurationData.is_active) {
        fetchConfigurations();
      }
    } catch (error) {
      console.error('Error saving AI configuration:', error);
      console.error('Error details:', error.response?.data);
      console.error('Status:', error.response?.status);
      console.error('URL:', error.config?.url);
      alert('Error saving configuration: ' + (error.response?.data?.error || error.response?.statusText || error.message));
    }
  };

  const deleteConfiguration = async (configurationId) => {
    if (!window.confirm('Are you sure you want to delete this AI configuration?')) {
      return;
    }

    try {
      await api.delete(`/ai-configurations/${configurationId}`);
      setConfigurations(configurations.filter(config => config.id !== configurationId));
    } catch (error) {
      console.error('Error deleting AI configuration:', error);
      alert('Error deleting configuration: ' + (error.response?.data?.error || error.message));
    }
  };

  const setActiveConfiguration = async (configurationId) => {
    try {
      await api.post(`/ai-configurations/${configurationId}/activate`);
      // Refresh configurations to update active status
      fetchConfigurations();
    } catch (error) {
      console.error('Error setting active configuration:', error);
      alert('Error activating configuration: ' + (error.response?.data?.error || error.message));
    }
  };

  const testConfiguration = async (configuration) => {
    setTestingConfiguration(configuration.id);
    try {
      // Temporarily activate this configuration for testing
      const wasActive = configurations.find(c => c.is_active);
      await api.post(`/ai-configurations/${configuration.id}/activate`);
      
      const response = await api.get('/test-claude');
      
      if (response.data.success) {
        alert(`✅ ${configuration.name} test successful!\\n\\nAPI Type: ${response.data.api_type}\\n\\nThe API is working correctly.`);
      } else {
        alert(`❌ ${configuration.name} test failed:\\n\\n${response.data.error}`);
      }
      
      // Restore previous active configuration if it was different
      if (wasActive && wasActive.id !== configuration.id) {
        await api.post(`/ai-configurations/${wasActive.id}/activate`);
      }
      
      fetchConfigurations();
    } catch (error) {
      console.error('Error testing configuration:', error);
      alert(`❌ Test failed:\\n\\n${error.response?.data?.error || error.message}`);
    } finally {
      setTestingConfiguration(null);
    }
  };

  const editConfiguration = (configuration) => {
    setEditingConfiguration(configuration);
    setShowModal(true);
  };

  const formatApiType = (apiType) => {
    switch (apiType) {
      case 'claude':
        return '🤖 Claude';
      case 'openai':
        return '🧠 OpenAI';
      case 'custom':
        return '⚙️ Custom';
      default:
        return apiType;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div>Loading AI configurations...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <div>
          <h2>AI Configuration</h2>
          <p>Manage AI services for image import and item extraction</p>
        </div>
        <button className="btn" onClick={() => setShowModal(true)}>
          Add Configuration
        </button>
      </div>

      <div className="card">
        <h3>Active Configuration</h3>
        {(() => {
          const activeConfig = configurations.find(config => config.is_active);
          if (activeConfig) {
            return (
              <div style={{ padding: '1rem', backgroundColor: '#e8f5e8', borderRadius: '8px', border: '2px solid #27ae60' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h4 style={{ margin: '0 0 0.5rem 0', color: '#27ae60' }}>
                      ✅ {activeConfig.name}
                    </h4>
                    <p style={{ margin: '0', color: '#666' }}>
                      {formatApiType(activeConfig.api_type)} • {activeConfig.model_name || 'Default model'}
                    </p>
                  </div>
                  <button 
                    className="btn" 
                    onClick={() => testConfiguration(activeConfig)}
                    disabled={testingConfiguration === activeConfig.id}
                    style={{ backgroundColor: '#27ae60' }}
                  >
                    {testingConfiguration === activeConfig.id ? 'Testing...' : 'Test API'}
                  </button>
                </div>
              </div>
            );
          } else {
            return (
              <div style={{ padding: '1rem', backgroundColor: '#ffeaa7', borderRadius: '8px', border: '2px solid #fdcb6e' }}>
                <p style={{ margin: '0', color: '#d63031' }}>
                  ⚠️ No active configuration. Please activate a configuration to use image import.
                </p>
              </div>
            );
          }
        })()}
      </div>

      <div className="card">
        <h3>All Configurations ({configurations.length})</h3>
        {configurations.length === 0 ? (
          <p>No AI configurations yet. Add your first configuration to get started with image import.</p>
        ) : (
          <div className="table-container">
            <table className="configurations-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Model</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {configurations.map(config => (
                  <tr key={config.id}>
                    <td>
                      <strong>{config.name}</strong>
                      {config.is_active && (
                        <span style={{ 
                          marginLeft: '0.5rem', 
                          padding: '0.2rem 0.5rem', 
                          backgroundColor: '#27ae60', 
                          color: 'white', 
                          borderRadius: '12px', 
                          fontSize: '0.75em',
                          fontWeight: 'bold'
                        }}>
                          ACTIVE
                        </span>
                      )}
                    </td>
                    <td>{formatApiType(config.api_type)}</td>
                    <td>
                      {config.model_name ? (
                        <code style={{ fontSize: '0.85em', backgroundColor: '#f8f9fa', padding: '2px 6px', borderRadius: '4px' }}>
                          {config.model_name}
                        </code>
                      ) : (
                        <span style={{ color: '#888' }}>Default</span>
                      )}
                    </td>
                    <td>
                      {config.has_api_key ? (
                        <span style={{ color: '#27ae60' }}>✅ Configured</span>
                      ) : (
                        <span style={{ color: '#e74c3c' }}>❌ No API Key</span>
                      )}
                    </td>
                    <td style={{ fontSize: '0.9em', color: '#666' }}>
                      {formatDate(config.created_at)}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button 
                          className="btn btn-small" 
                          onClick={() => testConfiguration(config)}
                          disabled={testingConfiguration === config.id}
                          title="Test API connection"
                        >
                          {testingConfiguration === config.id ? '⏳' : '🧪'}
                        </button>
                        {!config.is_active && (
                          <button 
                            className="btn btn-small" 
                            onClick={() => setActiveConfiguration(config.id)}
                            title="Set as active"
                            style={{ backgroundColor: '#3498db' }}
                          >
                            Activate
                          </button>
                        )}
                        <button 
                          className="btn btn-small" 
                          onClick={() => editConfiguration(config)}
                          title="Edit configuration"
                        >
                          Edit
                        </button>
                        <button 
                          className="btn btn-small btn-danger" 
                          onClick={() => deleteConfiguration(config.id)}
                          disabled={config.is_active}
                          title={config.is_active ? "Cannot delete active configuration" : "Delete configuration"}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <AIConfigurationModal
          configuration={editingConfiguration}
          onSave={saveConfiguration}
          onClose={() => {
            setShowModal(false);
            setEditingConfiguration(null);
          }}
        />
      )}
    </div>
  );
}

export default AIConfiguration;